import { useState, type ReactNode } from 'react'
import { Check, Eye, EyeOff } from 'lucide-react'
import {
  Button,
  Input,
  Field,
  FieldLabel,
  FieldGroup,
} from '@/components/ui'

export interface CopilotApiKeySectionProps {
  title: string
  description: string
  hasKey: boolean
  isLoading: boolean
  isSaving: boolean
  isDeleting: boolean
  onSave: (apiKey: string) => Promise<void>
  onDelete: () => Promise<void>
  fallbackInfo?: ReactNode
  testIdPrefix?: string
  credentialNoun?: string
  credentialNounLabel?: string
  helpContent?: ReactNode
}

const DEFAULT_HELP_CONTENT = (
  <div className="space-y-2">
    <p className="font-medium text-foreground">Generate a GitHub personal access token</p>
    <ol className="list-decimal space-y-1 pl-5">
      <li>
        Go to{' '}
        <a
          href="https://github.com/settings/personal-access-tokens"
          target="_blank"
          rel="noopener noreferrer"
          className="underline hover:text-foreground"
        >
          github.com/settings/personal-access-tokens
        </a>{' '}
        and click <span className="font-medium text-foreground">Generate new token</span>.
      </li>
      <li>
        Under <span className="font-medium text-foreground">Token type</span>, choose a{' '}
        <span className="font-medium text-foreground">fine-grained</span> personal access token (not a classic token).
      </li>
      <li>
        Set <span className="font-medium text-foreground">Repository access</span> to{' '}
        <span className="font-medium text-foreground">Public repositories</span>.
      </li>
      <li>
        Under <span className="font-medium text-foreground">Permissions → Add permissions</span>, add{' '}
        <span className="font-medium text-foreground">Copilot Requests</span> and set it to{' '}
        <span className="font-medium text-foreground">Read access</span>.
      </li>
      <li>
        Click <span className="font-medium text-foreground">Generate token</span>, then copy the token (it starts with{' '}
        <code className="rounded bg-muted px-1 py-0.5 font-mono text-xs">github_pat_</code>) and paste it above.
      </li>
    </ol>
  </div>
)

export function CopilotApiKeySection({
  title,
  description,
  hasKey,
  isLoading,
  isSaving,
  isDeleting,
  onSave,
  onDelete,
  fallbackInfo,
  testIdPrefix = 'copilot-api-key',
  credentialNoun = 'GitHub token',
  credentialNounLabel = 'GitHub Token',
  helpContent,
}: CopilotApiKeySectionProps) {
  const [apiKey, setApiKey] = useState('')
  const [showKey, setShowKey] = useState(false)
  const [keyError, setKeyError] = useState<string | null>(null)

  const inputId = `${testIdPrefix}-input`
  const resolvedHelpContent = helpContent === undefined ? DEFAULT_HELP_CONTENT : helpContent

  const handleSave = async () => {
    setKeyError(null)

    const trimmedKey = apiKey.trim()

    if (!trimmedKey) {
      setKeyError(`${credentialNounLabel} is required.`)
      return
    }

    if (trimmedKey.length < 4) {
      setKeyError(`${credentialNounLabel} is too short.`)
      return
    }

    if (!trimmedKey.startsWith('github_pat_')) {
      setKeyError(`${credentialNounLabel} must be a fine-grained personal access token starting with 'github_pat_'.`)
      return
    }

    try {
      await onSave(trimmedKey)
      setApiKey('')
      setShowKey(false)
    } catch {
      setKeyError(`Failed to save ${credentialNoun}. Please try again.`)
    }
  }

  const handleDelete = async () => {
    try {
      await onDelete()
    } catch {
      setKeyError(`Failed to remove ${credentialNoun}. Please try again.`)
    }
  }

  return (
    <section aria-label={title} data-testid={`${testIdPrefix}-section`}>
      <h2 className="text-xl font-bold mb-2 my-8">{title}</h2>
      <hr className="my-2" />
      <p className="mb-6 text-sm text-muted-foreground">{description}</p>

      {fallbackInfo && (
        <div className="mb-4" data-testid={`${testIdPrefix}-fallback-info`}>
          {fallbackInfo}
        </div>
      )}

      {isLoading ? (
        <p role="status" aria-live="polite" className="text-sm text-muted-foreground">
          Loading...
        </p>
      ) : hasKey ? (
        <>
          <FieldGroup>
            <Field>
              <FieldLabel htmlFor={inputId}>{credentialNounLabel}</FieldLabel>
              <div className="relative">
                <Input
                  id={inputId}
                  type="text"
                  value="••••••••••••••••••••••••"
                  disabled
                  className="text-muted-foreground"
                  data-testid={`${testIdPrefix}-input`}
                />
              </div>
            </Field>
          </FieldGroup>
          <div className="mt-6 flex justify-end gap-3">
            <Button
              variant="outline"
              onClick={handleDelete}
              disabled={isDeleting}
              data-testid={`${testIdPrefix}-delete-button`}
            >
              {isDeleting ? 'Removing...' : `Remove ${credentialNoun}`}
            </Button>
          </div>
        </>
      ) : (
        <div data-testid={`${testIdPrefix}-manual-section`}>
          <FieldGroup>
            <Field>
              <FieldLabel htmlFor={inputId}>{credentialNounLabel}</FieldLabel>
              <div className="relative">
                <Input
                  id={inputId}
                  type={showKey ? 'text' : 'password'}
                  placeholder="github_pat_..."
                  value={apiKey}
                  onChange={e => {
                    setApiKey(e.target.value)
                    setKeyError(null)
                  }}
                  className="pr-10"
                  data-testid={`${testIdPrefix}-input`}
                />
                {apiKey && (
                  <button
                    type="button"
                    onClick={() => setShowKey(v => !v)}
                    className="absolute inset-y-0 right-0 flex items-center px-3 text-muted-foreground hover:text-foreground"
                    aria-label={showKey ? `Hide ${credentialNoun}` : `Show ${credentialNoun}`}
                  >
                    {showKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                )}
              </div>
              {keyError && (
                <p role="alert" className="text-sm text-destructive" data-testid={`${testIdPrefix}-error`}>
                  {keyError}
                </p>
              )}
              {resolvedHelpContent && (
                <p className="text-sm text-muted-foreground">{resolvedHelpContent}</p>
              )}
            </Field>
          </FieldGroup>

          <div className="mt-6 flex justify-end gap-3">
            <Button
              onClick={handleSave}
              disabled={isSaving}
              data-testid={`${testIdPrefix}-save-button`}
            >
              <Check />
              {isSaving ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>
        </div>
      )}
    </section>
  )
}
